/**
 * HeroVaultBackdrop.jsx — golden-vault backdrop + card-suit particle layer
 *
 * Rendered as an absolutely-positioned layer behind the hero content.
 * Two visual pieces:
 *   1. `.jr-vault-doors` — radial + linear gradients that fake the
 *      open-vault glow from the concept render (deep-black frame with
 *      an ethereal gold burst breaking through the center).
 *   2. `.jr-suit-particles` — 24 floating card-suit glyphs (♠♥♦♣) that
 *      drift up-and-down at slow, staggered phases. Pure CSS, no JS
 *      animation loop, so it stays cheap on mobile.
 *
 * Uses `pointer-events: none` so it never intercepts clicks on the
 * hero islands / King mascot.
 */
import { useMemo } from "react";

const SUITS = ["♠", "♥", "♦", "♣"];

export default function HeroVaultBackdrop({ suitCount = 24 }) {
  const suits = useMemo(() => {
    return Array.from({ length: suitCount }).map((_, i) => {
      const rand = (min, max) => min + Math.random() * (max - min);
      const suit = SUITS[i % SUITS.length];
      const isRed = suit === "♥" || suit === "♦";
      return {
        id:    i,
        char:  suit,
        left:  rand(2, 96),               // %
        top:   rand(4, 92),               // %
        size:  rand(14, 36),              // px
        delay: rand(0, 6),                // s
        dur:   rand(6, 12),               // s
        color: isRed ? "rgba(244, 63, 94, 0.28)" : "rgba(255, 215, 0, 0.32)",
      };
    });
  }, [suitCount]);

  return (
    <div className="jr-vault absolute inset-0 pointer-events-none overflow-hidden" aria-hidden data-testid="hero-vault-backdrop">
      {/* Vault frame — dark radial vignette with warm gold burst in the middle */}
      <div className="jr-vault-doors" />
      {/* Left + right "door" columns */}
      <div className="jr-vault-door jr-vault-door-l" />
      <div className="jr-vault-door jr-vault-door-r" />
      {/* Central beam */}
      <div className="jr-vault-beam" />

      {/* Card-suit particles */}
      <div className="jr-suit-particles absolute inset-0">
        {suits.map((s) => (
          <span
            key={s.id}
            className="jr-suit"
            style={{
              left:            `${s.left}%`,
              top:             `${s.top}%`,
              fontSize:        `${s.size}px`,
              color:           s.color,
              animationDelay:  `${s.delay}s`,
              animationDuration: `${s.dur}s`,
            }}
          >
            {s.char}
          </span>
        ))}
      </div>

      <style>{`
        /* ═══ Vault backdrop ═══ */
        .jr-vault-doors {
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse at 50% 45%,
              rgba(255, 215, 0, 0.35) 0%,
              rgba(255, 165, 0, 0.18) 22%,
              rgba(120, 60, 0, 0.05) 45%,
              rgba(0, 0, 0, 0) 75%),
            radial-gradient(circle at 50% 40%,
              rgba(255, 245, 200, 0.22) 0%,
              rgba(0, 0, 0, 0) 40%);
        }
        .jr-vault-door {
          position: absolute;
          top: 0;
          bottom: 0;
          width: 22%;
          background: linear-gradient(180deg,
            rgba(255, 215, 0, 0.12) 0%,
            rgba(120, 60, 0, 0.08) 45%,
            rgba(0, 0, 0, 0.0) 100%);
          filter: blur(2px);
          opacity: 0.55;
        }
        .jr-vault-door-l { left: 4%;  border-right: 1px solid rgba(255,215,0,0.12); }
        .jr-vault-door-r { right: 4%; border-left:  1px solid rgba(255,215,0,0.12); }
        .jr-vault-beam {
          position: absolute;
          left: 50%;
          top: 0;
          bottom: 0;
          width: 32%;
          transform: translateX(-50%);
          background: linear-gradient(180deg,
            rgba(255, 245, 200, 0.20) 0%,
            rgba(255, 215, 0, 0.10) 40%,
            rgba(0, 0, 0, 0) 80%);
          filter: blur(30px);
        }

        /* ═══ Card-suit particles ═══ */
        @keyframes jr-suit-float {
          0%   { transform: translateY(0) rotate(-4deg);  opacity: 0.35; }
          50%  { transform: translateY(-24px) rotate(4deg); opacity: 0.9; }
          100% { transform: translateY(0) rotate(-4deg);  opacity: 0.35; }
        }
        .jr-suit {
          position: absolute;
          animation-name: jr-suit-float;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
          text-shadow: 0 0 8px currentColor;
          font-weight: 900;
          user-select: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .jr-suit { animation: none; opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}
